<template>
  <riseup-header
    :flow-config-id="FlowConfigIdType.RiseupWL"
    :force-background-color="'#012577'"
    :is-green="true"
  />
  <div
    v-if="showContentLoader"
    class="loader-wrapper"
  >
    <tiny-loader
      class="loader-class"
      variant="secondary"
    />
  </div>
  <div
    v-else
    class="signup"
  >
    <Descope
      :client="{ anonymousUserId: anonymousSession.getId(), flowConfigId, promptId, utmParams, contextPageReferrer }"
      :error-transformer="errorTransformer"
      :flow-id="descopeFlow"
      style-id="salaryFinance"
      @error="handleDescopeError"
      @success="handleSuccess"
    />
  </div>
</template>

<script lang="ts" setup>
import { Descope, useSession } from '@descope/vue-sdk';
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import _ from 'lodash';
import { computed, onBeforeMount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DDLogs from '../../DDLogs';
import Segment from '../../Segment';
import anonymousSession from '../../anonymousSession';
import useContentLoader from '../../composables/useContentLoader';
import { useOtsJob } from '../../services/otsConfigService';
import { FlowConfigIdType } from '../../types/otsConfig';
import { errorTransformer, handleDescopeError } from '../../utils/errorHandling';
import RiseupHeader from '../Common/RiseupHeader.vue';
import { completeDescopeOtsSignup } from '../../api/loginApi';

const { setOtsJobConfig } = useOtsJob();
const router = useRouter();
const {
  toggleLoader, showContentLoader,
} = useContentLoader();
toggleLoader(true);
watch(useSession().isLoading, async newVal => {
  toggleLoader(newVal);
});

const { sessionToken } = useSession();
const utmParams = _.pickBy(router.currentRoute.value.query, (value, key) => (key.startsWith('utm_') && value));
const flowConfigId = FlowConfigIdType.RiseupWL;
const promptId = router.currentRoute.value.query.pid || router.currentRoute.value.query.utm_pvr;
const progradProductId = router.currentRoute.value.query.productId;
const progradUserId = router.currentRoute.value.query.userId;
const contextPageReferrer = document.referrer;

const route = useRoute();

onBeforeMount(async () => {
  const queryParam = route.query || null;
  if (!queryParam['descope-login-flow']) {
    await Segment.reset();
    Segment.identifyAnonymousUser();
  }
});

const handleSuccess = async (e: CustomEvent) => {
  toggleLoader(true);
  DDLogs.info('DescopeOtsSignup handleSuccess');
  const token = sessionToken.value ?? e.detail.sessionJwt;

  if (!token) {
    throw new Error('Descope Signup flow failed to signup, user sessionJwt is empty');
  }
  const { product, etoroId } = router.currentRoute.value.query;
  const gclid = Cookies.get('gclid');

  const resp = await completeDescopeOtsSignup(
    token,
    utmParams,
    { flowConfigId, promptId },
    gclid,
    etoroId,
  );

  Segment.identifyCustomer(resp.customerId, resp.memberId);

  if (!e.detail.user.customAttributes.customerId) {
    Segment.trackUserGot('CustomerCreated_Marketing', {
      market: 'UK',
      product,
      gclid,
      customerId: resp.customerId,
      utmParams,
      otsParams: {
        flowConfigId,
        promptId,
      },
      etoroId,
    });
  }
  const { jobId } = resp;
  setOtsJobConfig(flowConfigId, jobId);
  if (router.currentRoute.value.query.utm_variant || router.currentRoute.value.query.utm_pvr) {
    DDLogs.warn(
      'OTS used with old utm_params',
      {
        customer_id: resp.customerId,
        utm_pvr: promptId,
        utm_var: flowConfigId,
      },
    );
  }

  const nextRouteQuery: Record<string, string> = {};
  if (progradProductId) {
    nextRouteQuery.productId = String(progradProductId);
  }
  if (progradUserId) {
    nextRouteQuery.userId = String(progradUserId);
  }

  router.push({
    path: '/ots/riseup-wl/connect',
    query: nextRouteQuery,
  });
};

const descopeFlow = computed(() => 'ots-riseup-wl-signup');

const onMetaBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return (userAgent.indexOf('FBAN') > -1) || (userAgent.indexOf('FBAV') > -1) || (userAgent.indexOf('Instagram') > -1);
};

const currentHash = window.location.hash;

if (onMetaBrowser() && !currentHash) {
  window.location.hash = '#FBK';
}

</script>

<style lang="scss" scoped>
@import '../../scss/riseup-vars';

.signup {
  overflow-y: auto;
  scroll-behavior: smooth;
  height: calc(100% - 59px);
  background-color: #FFFFFF;
}

.loader-wrapper {
  background-color: #EEF2F8;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

</style>
